import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";

export const fetchWarehouseDispatch = createAsyncThunk(
  "damage/fetchDispatch",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    type,
    filters,
    warehouse_id,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    type?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any[];
    warehouse_id?: string;
  }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      if (search) params.append("search", search);
    if (type === "tag") params.append("type", "tag");
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      const filterPayload = FilterBuilder(filters ?? []);

      const body = {
        filters: filterPayload,
      ...(type === "tag" ? { type: "tag" } : {}),
      };

      const response: any = await Axios.post(
        `${
          API_ENDPOINT?.WAREHOUSE?.DISPATCH?.LIST
        }?warehouse_id=${warehouse_id}&${params.toString()}`,
        body
      );
      return {
        data: response.data.data || [],
        rowCount: response.data.count || 0,
      };
    } catch (error: any) {
      throw new Error("Failed to fetch Dispatch List");
    }
  }
);

export const exportCSVDispatch = async ({
  filters,
  warehouse_id,
}: {
  filters?: {
    filterField: string;
    filterValue: any;
  }[];
  warehouse_id?: string;
}) => {
  const params = new URLSearchParams();
  // params.append("export", "excel");

  const filterPayload = FilterBuilder(filters ?? []);

  const body = {
    filters: filterPayload,
  };

  const response = await Axios.post(
    `${
      API_ENDPOINT?.WAREHOUSE?.DISPATCH?.EXPORT
    }?warehouse_id=${warehouse_id}&${params.toString()}`,
    body,
    { responseType: "blob" }
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") ||
      `dispatch_${new Date().toISOString().split("T")[0]}.csv`,
  };
};

export const fetchDispatchId = createAsyncThunk(
  "dispatch/fetchDispatchId",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `${API_ENDPOINT?.WAREHOUSE?.DISPATCH?.DETAIL(id)}`
      );
      return response?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch dispatch by ID"
      );
    }
  }
);

export const fetchDispatchDropdown = createAsyncThunk(
  "dispatch/fetchDispatchDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.WAREHOUSE?.DISPATCH?.DROPDOWN}`
      );
      return response?.data?.data || [];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch dispatch dropdown"
      );
    }
  }
);

export const updateDispatch = createAsyncThunk(
  "dispatch/updateDispatch",
  async ({ formData: data, dispatch_id }: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.put(
        `${API_ENDPOINT?.WAREHOUSE?.DISPATCH?.UPDATE(dispatch_id)}`,
        data
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update dispatch");
    }
  }
);

export const deliveryDoneDispatch = createAsyncThunk(
  "dispatch/updateDispatch",
  async ({ data, dispatch_id }: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.patch(
        `${API_ENDPOINT?.WAREHOUSE?.DISPATCH?.DELIVERY_DONE(dispatch_id)}`,
        data
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to dispatch Delivery Done "
      );
    }
  }
);

export const revertDispatch = createAsyncThunk(
  "dispatch/revertDispatch",
  async (dispatch_id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        `${API_ENDPOINT?.WAREHOUSE?.DISPATCH?.REVERT_DISPATCH(dispatch_id)}`
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to revert dispatch");
    }
  }
);

export const updateStatusDispatch = createAsyncThunk(
  "dispatch/updateStatusDispatch",
  async (dispatch_id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.patch(
        `${API_ENDPOINT?.WAREHOUSE?.DISPATCH?.STATUS_UPDATE(dispatch_id)}`
      );
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update dispatch status"
      );
    }
  }
);

export const createDispatch = createAsyncThunk(
  "dispatch/createDispatch",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        `${API_ENDPOINT?.WAREHOUSE?.DISPATCH?.CREATE}`,
        data
      );
      return response?.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to create dispatch");
    }
  }
);

export const deleteDispatch = createAsyncThunk(
  "dispatch/deleteDispatch",
  async (dispatch_id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.delete(
        `${API_ENDPOINT?.WAREHOUSE?.DISPATCH?.DELETE(dispatch_id)}`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to delete dispatch");
    }
  }
);

export const fetchSODropdownForDispatch = createAsyncThunk(
  "dispatch/fetchSODropdownForDispatch",
  async (warehouse_id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.WAREHOUSE?.DISPATCH?.SO_DROPDOWN_FOR_DISPATCH(
          warehouse_id
        )}`
      );
      return response?.data?.data || [];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch SO NO, Dropdown"
      );
    }
  }
);

export const fetchSODataForDispatch = createAsyncThunk(
  "dispatch/fetchSODataForDispatch",
  async (sales_order_product_id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.WAREHOUSE?.DISPATCH?.SO_AND_SHIPPING_ADDRESS_DROPDOWN_FOR_DISPATCH(
          sales_order_product_id
        )}`
      );
      return response?.data?.data || [];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data ||
          "Failed to fetch SO Shipping address Dropdown and SO data"
      );
    }
  }
);

export const fetchPackingDropdownBySOAndShippingAddress = createAsyncThunk(
  "dispatch/fetchPackingDropdownBySOAndShippingAddress",
  async (
    {
      sales_order_product_id,
      shipping_address_id,
    }: { sales_order_product_id: string; shipping_address_id: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.WAREHOUSE?.DISPATCH?.PACKING_DROPDOWN_BY_SO_AND_SHIPPING(
          sales_order_product_id,
          shipping_address_id
        )}`
      );
      return response?.data?.data || [];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data ||
          "Failed to fetch Packing Dropdown by SO and Shipping Address"
      );
    }
  }
);

export const fetchPackingDropdownBySOAndShippingAddressForEditDispatch =
  createAsyncThunk(
    "dispatch/fetchPackingDropdownBySOAndShippingAddressForEditDispatch",
    async (
      {
        sales_order_product_id,
        shipping_address_id,
      }: { sales_order_product_id: string; shipping_address_id: string },
      { rejectWithValue }
    ) => {
      try {
        const response = await Axios.get(
          `${API_ENDPOINT?.WAREHOUSE?.DISPATCH?.PACKING_DROPDOWN_BY_SO_AND_SHIPPING_FOR_EDIT_DISPATCH(
            sales_order_product_id,
            shipping_address_id
          )}`
        );
        return response?.data?.data || [];
      } catch (err: any) {
        return rejectWithValue(
          err.response?.data ||
            "Failed to fetch Packing Dropdown by SO and Shipping Address"
        );
      }
    }
  );

export const fetchPackingProductsByPackingIDs = createAsyncThunk(
  "dispatch/fetchPackingProductsByPackingIDs",
  async (packingIds: any[], { rejectWithValue }) => {
    try {
      // TODO: Implement endpoint for fetching packing products by packing IDs
      // For now, return empty array since endpoint doesn't exist
      const response = await Axios.get(
        `${API_ENDPOINT?.WAREHOUSE?.DISPATCH?.PACKING_PRODUCTS_BY_PACKING_IDS(packingIds)}`
      );
      return response?.data?.data || [];  
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Packing Products"
      );
    }
  }
);

export const downloadDispatchChallanPdf = createAsyncThunk(
  "dispatch/downloadDispatchChallanPdf",
  async (
    { dispatchId,dispatchNo }: { dispatchId: string;  dispatchNo: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await Axios.get(
        API_ENDPOINT?.WAREHOUSE?.DISPATCH?.PDF_CHALLAN(dispatchId),
        {
          responseType: "blob", // important for binary data
        }
      );

      // Create a URL for the blob and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Sales_Challan_${dispatchNo}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // cleanup

      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to download challan");
    }
  }
);
